<template>
  <!-- Drops are handled by Vuetify itself (VField drop → input.files → change → update:model-value);
       a second handler here would queue every dropped file twice. The bare modifiers only stop the
       browser from navigating to a file dropped outside the field. -->
  <v-file-input
    :model-value="modelValue"
    class="brand-file-drop rounded-lg"
    :accept="accept"
    :disabled="disabled"
    v-bind="$attrs" variant="outlined" prepend-icon=""
    :multiple="multiple" hide-details="auto"
    @drop.prevent
    @dragover.prevent
    @update:model-value="onFileInput"
    :clearable="false"
  >
    <template #prepend-inner>
      <slot>
        <div class="text-center mx-auto pa-6">
          <PhFileText class="text-teal400 text-h3 mb-4" weight="fill" />
          <h4 class="text-body-2 text-neutral900 font-weight-semibold" v-text="titleComputed" />
          <p v-if="subtitle" class="text-caption text-neutral400 mb-0" v-text="subtitle" />
        </div>
      </slot>
    </template>
  </v-file-input>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { PhFileText } from '@phosphor-icons/vue'

// Define component props
const props = defineProps({
  modelValue: {
    type: [File, Array],
    default: null,
  },
  accept: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  subtitle: {
    type: String,
    default: null,
  },
  multiple: Boolean,
  disabled: {
    type: Boolean,
    default: false,
  },
})

// Define emits
const emit = defineEmits(['update:model-value'])

// Define component options
defineOptions({
  inheritAttrs: false,
})

// Setup composables
const { t } = useI18n()

// Computed properties
const titleComputed = computed(() => {
  return props.title ?? t('component.filedropper.title')
})

// Methods
// Untyped files (some OSes report no MIME for .json/.csv) pass through — the consumer's own
// validation decides, exactly as it did for picked files before
const validateFile = (file) => {
  if (!props.accept || !file.type) return true

  return props.accept
    .replace(/\s/g, '').split(',')
    .some(accept => new RegExp(accept.replace('*', '.*')).test(file.type))
}

// Every path (picker or drop) ends here. The accept filter matters for drops only — the native
// <input accept> is not enforced on dropped files; nothing accepted leaves the model alone.
const onFileInput = (value) => {
  const files = (Array.isArray(value) ? value : [value]).filter(file => file && validateFile(file))
  if (!files.length) return

  emit('update:model-value', props.multiple ? files : files[0])
}
</script>

<style lang="scss" scoped>
.brand-file-drop {
  &:deep(.v-field__input) {
    display: none !important;
  }

  &:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral200));

      .v-field__outline__start {
        border-left-style: dashed;
        border-left-width: 2px;
        border-top-style: dashed;
        border-top-width: 2px;
        border-bottom-style: dashed;
        border-bottom-width: 2px;
      }

      .v-field__outline__notch {
        border-top-style: dashed;
        border-top-width: 2px;
      }

      .v-field__outline__end {
        border-right-style: dashed;
        border-right-width: 2px;
        border-top-style: dashed;
        border-top-width: 2px;
        border-bottom-style: dashed;
        border-bottom-width: 2px;
      }
    }
  }

  &.v-file-input--dragging :deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)),
  &:hover :deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-teal900));
    }
  }

  &:deep(.v-field) {
    display: grid;

    .v-field__prepend-inner {
      grid-column: 1 / -1;
      width: 100%;
      margin: 0;
      z-index: 1;
      pointer-events: none;
    }

    .v-field__field {
      @include overlay;

      cursor: pointer;
      grid-column: 1 / -1;

      .v-file-input__text {
        display: none;
      }
    }

    .v-field__append-inner {
      display: none;
    }
  }
}
</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .brand-file-drop:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }

  .brand-file-drop.v-file-input--dragging:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)),
  .brand-file-drop:hover:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-teal600)) !important;
    }
  }
}
</style>
